import React, { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Save, X } from "lucide-react";
import { toast } from "sonner";

const GRADE_OPTIONS = [
  { code: "", label: "— (no grade)" },
  { code: "HS", label: "High School" },
  { code: "SR+", label: "College" },
  { code: "ADULT", label: "Adult" },
];

export default function GroupEditDialog({ group, open, onOpenChange }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => ({
    subgroup: group.subgroup || "",
    group_leader: group.group_leader || "",
    head_counselor: group.head_counselor || "",
    assistants: [...(group.assistants || [])],
    cits: [...(group.cits || [])],
  }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        subgroup: group.subgroup || "",
        group_leader: group.group_leader || "",
        head_counselor: group.head_counselor || "",
        assistants: [...(group.assistants || [])],
        cits: [...(group.cits || [])],
      });
    }
  }, [open, group]);

  const updateAsst = (i, field, value) => {
    setForm((f) => ({
      ...f,
      assistants: f.assistants.map((a, idx) => (idx === i ? { ...a, [field]: value } : a)),
    }));
  };
  const addAsst = () =>
    setForm((f) => ({ ...f, assistants: [...f.assistants, { name: "", grade: "" }] }));
  const removeAsst = (i) =>
    setForm((f) => ({ ...f, assistants: f.assistants.filter((_, idx) => idx !== i) }));

  const updateCit = (i, field, value) => {
    setForm((f) => ({
      ...f,
      cits: f.cits.map((c, idx) => (idx === i ? { ...c, [field]: value } : c)),
    }));
  };
  const addCit = () =>
    setForm((f) => ({ ...f, cits: [...f.cits, { name: "", grade: "" }] }));
  const removeCit = (i) =>
    setForm((f) => ({ ...f, cits: f.cits.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      // Drop entries with empty names (nothing to show)
      const clean = {
        subgroup: form.subgroup,
        group_leader: form.group_leader,
        head_counselor: form.head_counselor,
        assistants: form.assistants.filter((a) => (a.name || "").trim()),
        cits: form.cits.filter((c) => (c.name || "").trim()),
      };
      await api.patch(`/groups/${group.code}`, clean);
      toast.success(`${group.code} saved`);
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["group", group.code] });
      onOpenChange(false);
    } catch (e) {
      toast.error(`Could not save ${group.code}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[95vw] max-w-2xl max-h-[85vh] overflow-y-auto bg-[#FDF6EC] border-[#E9DDC8]"
        data-testid={`group-edit-dialog-${group.code}`}
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0B2545]">
            Edit Group {group.code}
          </DialogTitle>
          <DialogDescription className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
            {group.division} · {form.subgroup || "—"} · Edit any counselor name or grade
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <FieldInput
            label="Sub-group label"
            value={form.subgroup}
            onChange={(v) => setForm({ ...form, subgroup: v })}
            testid={`edit-${group.code}-subgroup`}
            placeholder="e.g. PreK Boys"
          />

          <FieldInput
            label="Head Counselor"
            value={form.head_counselor}
            onChange={(v) => setForm({ ...form, head_counselor: v })}
            testid={`edit-${group.code}-head`}
            placeholder="Head counselor name"
          />

          {/* Assistants */}
          <section>
            <RowHeader
              title="Assistant Counselors"
              onAdd={addAsst}
              testid={`edit-${group.code}-add-asst`}
            />
            <div className="space-y-2">
              {form.assistants.map((a, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    data-testid={`edit-${group.code}-asst-name-${i}`}
                    value={a.name}
                    onChange={(e) => updateAsst(i, "name", e.target.value)}
                    placeholder="Assistant name"
                    className="col-span-7 px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                  />
                  <select
                    data-testid={`edit-${group.code}-asst-grade-${i}`}
                    value={a.grade || ""}
                    onChange={(e) => updateAsst(i, "grade", e.target.value)}
                    className="col-span-4 px-2 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                  >
                    {GRADE_OPTIONS.map((g) => (
                      <option key={g.code} value={g.code}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                  <button
                    data-testid={`edit-${group.code}-asst-remove-${i}`}
                    onClick={() => removeAsst(i)}
                    className="col-span-1 inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#E9DDC8] text-[#B54223] hover:bg-[#F8E9E4]"
                    aria-label="Remove assistant"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {form.assistants.length === 0 && (
                <div className="text-[#13315C]/50 italic text-sm">
                  No assistants yet.
                </div>
              )}
            </div>
          </section>

          {/* CITs */}
          <section>
            <RowHeader
              title="CITs"
              onAdd={addCit}
              testid={`edit-${group.code}-add-cit`}
            />
            <div className="space-y-2">
              {form.cits.map((c, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <input
                    data-testid={`edit-${group.code}-cit-name-${i}`}
                    value={c.name}
                    onChange={(e) => updateCit(i, "name", e.target.value)}
                    placeholder="CIT name"
                    className="col-span-11 px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
                  />
                  <button
                    data-testid={`edit-${group.code}-cit-remove-${i}`}
                    onClick={() => removeCit(i)}
                    className="col-span-1 inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#E9DDC8] text-[#B54223] hover:bg-[#F8E9E4]"
                    aria-label="Remove CIT"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              {form.cits.length === 0 && (
                <div className="text-[#13315C]/50 italic text-sm">No CITs yet.</div>
              )}
            </div>
          </section>
        </div>

        <DialogFooter className="gap-2">
          <button
            data-testid={`edit-${group.code}-cancel`}
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-[#0B2545] font-semibold border border-[#E9DDC8] hover:border-[#0B2545]"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
          <button
            data-testid={`edit-${group.code}-save`}
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#0B2545] text-[#FDF6EC] font-semibold hover:bg-[#13315C] disabled:opacity-70"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving…" : "Save Group"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldInput({ label, value, onChange, testid, placeholder }) {
  return (
    <label className="block">
      <span className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
        {label}
      </span>
      <input
        data-testid={testid}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full px-3 py-2 rounded-md border border-[#E9DDC8] bg-white text-sm focus:outline-none focus:border-[#0B2545]"
      />
    </label>
  );
}

function RowHeader({ title, onAdd, testid }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <div className="font-mono-camp text-[11px] tracking-widest text-[#13315C]/60 uppercase">
        {title}
      </div>
      <button
        data-testid={testid}
        onClick={onAdd}
        className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full border border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-[#FDF6EC]"
      >
        <Plus className="w-3.5 h-3.5" /> Add
      </button>
    </div>
  );
}
